import React, { useState, useEffect } from "react";
import { Button, Col, Row, Form, FormGroup, Label, Input, Card, CardBody, Alert } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUpload,
    faSpinner,
    faFileExcel,
    faProjectDiagram,
    faLayerGroup,
    faCheck,
    faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import { useLoggedInApi } from "../../../../api";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import readXlsxFile from "read-excel-file";
import { readSheetNames } from "read-excel-file";
import { useImportProjects, useImportLots } from "../hooks";

export default function Import() {
    const apiClient = useLoggedInApi();
    const queryClient = useQueryClient();

    const [file, setFile] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [showResultDetails, setShowResultDetails] = useState(false);

    // Import status tracking
    const [importStatus, setImportStatus] = useState({
        projects: { status: 'idle', count: 0 },
        lots: { status: 'idle', count: 0, batchProgress: { current: 0, total: 0 } }
    });

    // Use the mutation hooks
    const importProjectsMutation = useImportProjects();
    const importLotsMutation = useImportLots();

    const handleFileChange = (e) => {
        if (e.target.files.length > 0) {
            setFile(e.target.files[0]);
            // Reset states when a new file is selected
            setUploadResult(null);
            setParsedData(null);
            setImportStatus({
                projects: { status: 'idle', count: 0 },
                lots: { status: 'idle', count: 0 }
            });
        }
    };

    // Parse the Excel file when it changes
    useEffect(() => {
        if (file) {
            setIsParsing(true);
            parseExcelFile(file)
                .then(result => {
                    if (result.success) {
                        setParsedData(result);
                    } else {
                        setParsedData(null);
                        toast.error(result.message);
                    }
                })
                .catch(error => {
                    console.error("Error parsing file:", error);
                    setParsedData(null);
                    toast.error("Failed to parse the Excel file. Please check the file format.");
                })
                .finally(() => {
                    setIsParsing(false);
                });
        }
    }, [file]);

    const parseExcelFile = async (file) => {
        try {
            // Read sheet names
            const sheetNames = await readSheetNames(file);

            // Check if required sheets exist
            const requiredSheets = [
                "Project_File",
                // "Lot_File"
            ];
            const missingSheets = requiredSheets.filter(sheet => !sheetNames.includes(sheet));

            if (missingSheets.length > 0) {
                return {
                    success: false,
                    message: `Missing required sheets: ${missingSheets.join(", ")}`,
                    details: { missingSheets },
                    projects: [],
                    lots: []
                };
            }

            // Read Project_File sheet
            const projectRows = await readXlsxFile(file, { sheet: 'Project_File' });
            const projectHeaders = projectRows[0];

            // Check if required project headers exist
            const requiredProjectHeaders = ["pjname", "pj_id"];
            const missingProjectHeaders = requiredProjectHeaders.filter(
                header => !projectHeaders.includes(header)
            );

            if (missingProjectHeaders.length > 0) {
                return {
                    success: false,
                    message: `Missing required headers in Project_File sheet: ${missingProjectHeaders.join(", ")}`,
                    details: { missingProjectHeaders },
                    projects: [],
                    lots: []
                };
            }

            // // Read Lot_File sheet
            // const lotRows = await readXlsxFile(file, { sheet: 'Lot_File' });
            // const lotHeaders = lotRows[0];
            //
            // // Check if required lot headers exist
            // const requiredLotHeaders = ["lot_cn"];
            // const missingLotHeaders = requiredLotHeaders.filter(
            //     header => !lotHeaders.includes(header)
            // );
            //
            // if (missingLotHeaders.length > 0) {
            //     return {
            //         success: false,
            //         message: `Missing required headers in Lot_File sheet: ${missingLotHeaders.join(", ")}`,
            //         details: { missingLotHeaders },
            //         projects: [],
            //         lots: []
            //     };
            // }

            // Parse project data
            const projects = projectRows.slice(1).map(row => {
                const project = {};
                projectHeaders.forEach((header, index) => {
                    if (header === "pjname") {
                        project["name"] = row[index];
                    } else if (header === "pj_id") {
                        project["ac_id"] = row[index];
                    } else {
                        project[header] = row[index];
                    }
                });
                return project;
            }).filter(project => project.name && project.ac_id); // Filter out rows with missing required fields

            // // Parse lot data
            // const lots = lotRows.slice(1).map(row => {
            //     const lot = {};
            //     lotHeaders.forEach((header, index) => {
            //         lot[header] = row[index];
            //     });
            //
            //     // Parse lot_cn to extract project's ac_id
            //     if (lot.lot_cn && lot.lot_cn.length >= 3) {
            //         const projectCode = lot.lot_cn.substring(0, 3);
            //         // Find the project with matching ac_id
            //         const matchingProject = projects.find(p => p.ac_id === projectCode);
            //         if (matchingProject) {
            //             lot.payment_project = matchingProject.ac_id;
            //         }
            //     }
            //
            //     return lot;
            // }).filter(lot => lot.lot_cn); // Filter out rows with missing required fields

            return {
                success: true,
                projects,
                // lots
            };
        } catch (error) {
            console.error("Error parsing Excel file:", error);
            return {
                success: false,
                message: "Error parsing Excel file",
                details: { error: error.message },
                projects: [],
                lots: []
            };
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!file) {
            toast.error("Please select a file to import");
            return;
        }

        if (!parsedData || !parsedData.success) {
            toast.error("Please wait for the file to be parsed or select a valid file");
            return;
        }

        // Reset upload result
        setUploadResult(null);

        // Get the parsed data
        const { projects, lots } = parsedData;

        // Check if there's data to import
        if (projects.length === 0 && lots.length === 0) {
            setUploadResult({
                success: false,
                message: "No valid data found in the file",
                details: { error: "No valid projects or lots found" }
            });
            return;
        }

        // Update status to show we're starting
        setImportStatus({
            projects: { status: projects.length > 0 ? 'pending' : 'skipped', count: projects.length },
            lots: { status: 'waiting', count: lots?.length }
        });

        try {
            // Step 1: Import projects first
            if (projects.length > 0) {
                // Update status
                setImportStatus(prev => ({
                    ...prev,
                    projects: { ...prev.projects, status: 'importing' }
                }));

                // Import projects
                await importProjectsMutation.mutateAsync(projects);

                // Update status
                setImportStatus(prev => ({
                    ...prev,
                    projects: { ...prev.projects, status: 'completed' },
                    lots: { ...prev.lots, status: 'completed' }  // fake completion for lots
                }));
            }

            // // Step 2: Import lots
            // if (lots.length > 0) {
            //     // Update status
            //     setImportStatus(prev => ({
            //         ...prev,
            //         lots: {
            //             ...prev.lots,
            //             status: 'importing',
            //             batchProgress: { current: 0, total: Math.ceil(lots.length / 1000) }
            //         }
            //     }));
            //
            //     // Import lots with batch progress tracking
            //     await importLotsMutation.mutateAsync({
            //         lots,
            //         onBatchProgress: (progress) => {
            //             setImportStatus(prev => ({
            //                 ...prev,
            //                 lots: {
            //                     ...prev.lots,
            //                     batchProgress: progress
            //                 }
            //             }));
            //         }
            //     });
            //
            //     // Update status
            //     setImportStatus(prev => ({
            //         ...prev,
            //         lots: { ...prev.lots, status: 'completed' }
            //     }));
            // }

            // Set final success result
            setUploadResult({
                success: true,
                message: "Import completed successfully",
                details: {
                    projects: {
                        count: projects.length,
                        status: projects.length > 0 ? 'completed' : 'skipped'
                    },
                    // lots: {
                    //     count: lots.length,
                    //     status: lots.length > 0 ? 'completed' : 'skipped'
                    // }
                }
            });

            // Show overall success message
            toast.success("Import completed successfully");

            // Reset file input
            setFile(null);
            document.getElementById("importFile").value = "";

        } catch (error) {
            console.error("Error during import:", error);

            // Determine which step failed
            const failedStep = importStatus.projects.status !== 'completed' ? 'projects' : 'lots';

            // Update status to show failure
            setImportStatus(prev => ({
                ...prev,
                [failedStep]: { ...prev[failedStep], status: 'failed' }
            }));

            // Set error result
            setUploadResult({
                success: false,
                message: `Failed to import ${failedStep}`,
                details: error.response?.data || { error: "Unknown error occurred" }
            });
        }
    };

    return (
        <>
            <Row>
                <Col>
                    <h3 className="page-header pb-2">Data Import</h3>
                </Col>
            </Row>
            <Row className="mt-4">
                <Col>
                    Import data for projects. Upload CSV or Excel files in the required format.
                </Col>
            </Row>

            <Row className="mt-5">
                <Col md={8}>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit}>
                                <FormGroup>
                                    <Label for="importFile">Select Excel File</Label>
                                    <Input
                                        type="file"
                                        id="importFile"
                                        onChange={handleFileChange}
                                        accept=".xlsx, .xls"
                                        disabled={isParsing || importProjectsMutation.isLoading || importLotsMutation.isLoading}
                                    />
                                    <small className="text-muted">
                                        Accepted formats: Excel (.xlsx, .xls) with Project_File {/*and Lot_File*/} sheet
                                    </small>
                                </FormGroup>

                                {isParsing && (
                                    <Alert color="info" className="mt-3">
                                        <FontAwesomeIcon icon={faSpinner} spin />{" "}
                                        Parsing Excel file... Please wait.
                                    </Alert>
                                )}

                                {parsedData && parsedData.success && (
                                    <Alert color="success" className="mt-3">
                                        <h5 className="mb-3">
                                            <FontAwesomeIcon icon={faFileExcel} />{" "}
                                            File Summary
                                        </h5>
                                        <div className="ms-4">
                                            <div className="mb-2">
                                                <FontAwesomeIcon icon={faProjectDiagram} />{" "}
                                                <strong>Projects Detected:</strong> {parsedData.projects.length}
                                            </div>
                                            {/*<div>*/}
                                            {/*    <FontAwesomeIcon icon={faLayerGroup} />{" "}*/}
                                            {/*    <strong>Lots Detected:</strong> {parsedData.lots.length}*/}
                                            {/*</div>*/}
                                        </div>
                                    </Alert>
                                )}

                                {/* Import Status Display */}
                                {(importStatus.projects.status !== 'idle' || importStatus.lots.status !== 'idle') && (
                                    <Alert color="info" className="mt-3">
                                        <h5 className="mb-3">Import Progress</h5>
                                        <div className="ms-4">
                                            <div className="mb-2">
                                                <strong>Projects:</strong> {importStatus.projects.count} items
                                                {importStatus.projects.status === 'skipped' && (
                                                    <span className="text-muted ms-2">(Skipped - No projects to import)</span>
                                                )}
                                                {importStatus.projects.status === 'pending' && (
                                                    <span className="text-info ms-2">(Pending)</span>
                                                )}
                                                {importStatus.projects.status === 'importing' && (
                                                    <span className="text-primary ms-2">
                                                        <FontAwesomeIcon icon={faSpinner} spin />{" "}
                                                        (Importing...)
                                                    </span>
                                                )}
                                                {importStatus.projects.status === 'completed' && (
                                                    <span className="text-success ms-2">
                                                        <FontAwesomeIcon icon={faCheck} />{" "}
                                                        (Completed)
                                                    </span>
                                                )}
                                                {importStatus.projects.status === 'failed' && (
                                                    <span className="text-danger ms-2">(Failed)</span>
                                                )}
                                            </div>
                                            {/*<div>*/}
                                            {/*    <strong>Lots:</strong> {importStatus.lots.count} items*/}
                                            {/*    {importStatus.lots.status === 'skipped' && (*/}
                                            {/*        <span className="text-muted ms-2">(Skipped - No lots to import)</span>*/}
                                            {/*    )}*/}
                                            {/*    {importStatus.lots.status === 'waiting' && (*/}
                                            {/*        <span className="text-muted ms-2">(Waiting for projects to complete)</span>*/}
                                            {/*    )}*/}
                                            {/*    {importStatus.lots.status === 'pending' && (*/}
                                            {/*        <span className="text-info ms-2">(Pending)</span>*/}
                                            {/*    )}*/}
                                            {/*    {importStatus.lots.status === 'importing' && (*/}
                                            {/*        <span className="text-primary ms-2">*/}
                                            {/*            <FontAwesomeIcon icon={faSpinner} spin />{" "}*/}
                                            {/*            (Importing... Batch {importStatus.lots.batchProgress.current} of {importStatus.lots.batchProgress.total})*/}
                                            {/*        </span>*/}
                                            {/*    )}*/}
                                            {/*    {importStatus.lots.status === 'completed' && (*/}
                                            {/*        <span className="text-success ms-2">*/}
                                            {/*            <FontAwesomeIcon icon={faCheck} />{" "}*/}
                                            {/*            (Completed)*/}
                                            {/*        </span>*/}
                                            {/*    )}*/}
                                            {/*    {importStatus.lots.status === 'failed' && (*/}
                                            {/*        <span className="text-danger ms-2">(Failed)</span>*/}
                                            {/*    )}*/}
                                            {/*</div>*/}
                                        </div>
                                    </Alert>
                                )}

                                <Button 
                                    color="primary" 
                                    type="submit" 
                                    disabled={
                                        importProjectsMutation.isLoading || 
                                        importLotsMutation.isLoading || 
                                        !file || 
                                        isParsing || 
                                        !parsedData || 
                                        !parsedData.success
                                    }
                                    className="mt-3"
                                >
                                    {importProjectsMutation.isLoading || importLotsMutation.isLoading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin />{" "}
                                            Importing Data...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faUpload} />{" "}
                                            Import All Data
                                        </>
                                    )}
                                </Button>
                            </Form>

                            {uploadResult && (
                                <Alert 
                                    color={uploadResult.success ? "success" : "danger"}
                                    className="mt-4"
                                >
                                    <h5>
                                        {uploadResult.message}
                                        <FontAwesomeIcon icon={faInfoCircle} className={"ms-2 small opacity-50"}
                                            onClick={() => {
                                                setShowResultDetails(!showResultDetails);
                                            }}
                                        />
                                    </h5>
                                    {(uploadResult.details && showResultDetails) && (
                                        <div className="mt-2">
                                            <strong>Details:</strong>
                                            <pre className="mt-2" style={{ maxHeight: "200px", overflow: "auto" }}>
                                                {JSON.stringify(uploadResult.details, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </Alert>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                <Col md={4}>
                    <Card>
                        <CardBody>
                            <h5>Import Guidelines</h5>
                            <hr />
                            <p>Please follow these guidelines for a successful import:</p>
                            <ul>
                                <li>Use Excel (.xlsx) format</li>
                                <li>File must contain one sheet:{" "}
                                    <strong>Project_File</strong>
                                    {/*and <strong>Lot_File</strong>*/}
                                </li>
                                {/*<li>Ensure all required fields are included in each sheet</li>*/}
                                <li>For large files, the import may take some time</li>
                            </ul>

                            <h6 className="mt-4">Required Sheets and Fields</h6>
                            <p><strong>Project_File sheet:</strong></p>
                            <ul>
                                <li><strong>pjname</strong>: Project name (maps to "name" in API)</li>
                                <li><strong>pj_id</strong>: Project ID (maps to "ac_id" in API)</li>
                            </ul>

                            {/*<p><strong>Lot_File sheet:</strong></p>*/}
                            {/*<ul>*/}
                            {/*    <li><strong>lot_cn</strong>: Lot identifier (12 characters)</li>*/}
                            {/*    <li>The first 3 characters of lot_cn are used to match with a project's ac_id</li>*/}
                            {/*</ul>*/}

                            {/*<h6 className="mt-4">Simplified Import Process</h6>*/}
                            {/*<p>The system will automatically:</p>*/}
                            {/*<ol>*/}
                            {/*    <li>Parse both sheets from the Excel file</li>*/}
                            {/*    <li>Map the project data (pjname → name, pj_id → ac_id)</li>*/}
                            {/*    <li>Extract project code from lot_cn and link to projects</li>*/}
                            {/*    <li><strong>Import projects first</strong> to ensure they exist in the system</li>*/}
                            {/*    <li><strong>Then import lots</strong> that reference those projects:*/}
                            {/*        <ul>*/}
                            {/*            <li>Lots are sorted by lot_cn for consistent ordering</li>*/}
                            {/*            <li>Lots are processed in batches of 1000 to optimize performance</li>*/}
                            {/*            <li>Progress is shown for each batch during import</li>*/}
                            {/*        </ul>*/}
                            {/*    </li>*/}
                            {/*</ol>*/}

                            {/*<div className="alert alert-info mt-3 mb-0">*/}
                            {/*    <strong>Note:</strong> This sequential import process ensures data integrity by creating projects before the lots that reference them. Processing lots in batches improves reliability for large imports.*/}
                            {/*</div>*/}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    );
}
